    import moment from 'moment';
import { StudentAdmissionChangeUtils } from './index';

export class FeeHandlers {
    constructor(component) {
        this.component = component;
    }

    handlePercentageChange = (e, installmentIndex, rowData) => {
        try {
            const percentage = Number(e.target.value);

            if (percentage < 0 || percentage > 100) {
                this.component.showToast('warn', 'Warning', 'Percentage must be between 0 and 100');
                return;
            }

            const updatedInstallmentData = [...this.component.state.installmentData];
            const currentInstallment = updatedInstallmentData[installmentIndex];

            if (!currentInstallment?.feeTypes) {
                console.error('Invalid installment data');
                return;
            }

            // Find the specific fee type and update only that one
            const feeTypeIndex = currentInstallment.feeTypes.findIndex(fee =>
                fee.feeType === rowData.feeType &&
                fee.feeTypeId === rowData.feeTypeId
            );

            if (feeTypeIndex === -1) {
                console.error('Fee type not found');
                return;
            }

            // Calculate the fee amount based on percentage
            const calculatedFee = Math.round((rowData.totalFee * percentage) / 100);

            currentInstallment.feeTypes[feeTypeIndex] = {
                ...currentInstallment.feeTypes[feeTypeIndex],
                percentage,
                calculatedFee
            };

            this.component.setState({ installmentData: updatedInstallmentData });
        } catch (error) {
            console.error('Error in handlePercentageChange:', error);
            this.component.showToast('error', 'Error', 'Failed to update percentage');
        }
    };

    handleCalculatedFeeChange = (e, installmentIndex, rowData) => {
        try {
            if (rowData.isOneTime) {
                this.component.showToast('warn', 'Warning', 'Cannot modify calculated fee for one-time fees');
                return;
            }

            // Get and validate the input value
            const newCalculatedFee = Number(e.target.value);
            if (newCalculatedFee < 0 || newCalculatedFee > rowData.totalFee) {
                this.component.showToast('warn', 'Warning', 'Calculated fee cannot exceed total fee');
                return;
            }

            const updatedInstallmentData = [...this.component.state.installmentData];
            const currentInstallment = updatedInstallmentData[installmentIndex];

            if (!currentInstallment?.feeTypes) {
                console.error('Invalid installment data');
                return;
            }

            // Find the specific fee type using feeTypeId
            const feeTypeIndex = currentInstallment.feeTypes.findIndex(fee =>
                fee.feeType === rowData.feeType &&
                fee.feeTypeId === rowData.feeTypeId
            );

            if (feeTypeIndex === -1) {
                console.error('Fee type not found');
                return;
            }

            // Calculate percentage based on the new calculated fee
            const percentage = Math.round((newCalculatedFee * 100) / rowData.totalFee);

            // Update the fee type with new values
            currentInstallment.feeTypes[feeTypeIndex] = {
                ...currentInstallment.feeTypes[feeTypeIndex],
                percentage,
                calculatedFee: newCalculatedFee
            };

            this.component.setState({ installmentData: updatedInstallmentData });
        } catch (error) {
            console.error('Error in handleCalculatedFeeChange:', error);
            this.component.showToast('error', 'Error', 'Failed to update calculated fee');
        }
    };

    handleTotalFeeChange = (value, feeTypeIndex, feeTypeId, installmentId) => {
        this.component.setState(prevState => {
            // Update selectedFeeStructure array
            const updatedSelectedFeeStructure = prevState.selectedFeeStructure.map(category => {
                const updatedFeeTypes = category.feeTypes.map(fee => {
                    if (fee._id === feeTypeId) {
                        return {
                            ...fee,
                            totalFee: value,
                            percentage: 0,
                            calculatedFee: 0
                        };
                    }
                    return fee;
                });

                return {
                    ...category,
                    feeTypes: updatedFeeTypes
                };
            });

            // Update installmentData
            const updatedInstallmentData = prevState.installmentData.map(installment => {
                return {
                    ...installment,
                    feeTypes: installment.feeTypes.map(feeType => {
                        if (feeType._id !== feeTypeId) return feeType;
                        return {
                            ...feeType,
                            totalFee: value,
                            percentage: 0,
                            calculatedFee: 0
                        };
                    })
                };
            });

            return {
                selectedFeeStructure: updatedSelectedFeeStructure,
                installmentData: updatedInstallmentData
            };
        });
    };

    handleFeePlanChange = async () => {
        // feePlanInfo is an array, so we need to get the first element
        // All fee schedules for a student must use the same planId
        const feePlanInfoArray = this.component.state?.newFeeResponse?.feePlanInfo;
        const selectedFeePlan = Array.isArray(feePlanInfoArray) ? feePlanInfoArray[0] : feePlanInfoArray;
        
        if (!selectedFeePlan) return;

        // Get all fee schedules from the new structure
        const feeStructureDetails = this.component.state.rowData?.newFeeData?.feeSchedules || [];

        if (feeStructureDetails.length === 0) {
            console.warn('No fee schedules found');
            return;
        }

        // Get all fee category IDs from all schedules
        const allFeeCategoryIds = feeStructureDetails.map(schedule => schedule.feeCategory);

        // Get fee categories from newFeeResponse (populated in component state)
        const feeCategoriesFromResponse = this.component.state.newFeeResponse?.feeCategory || [];

        // Filter applicable fee categories from the response data
        let applicableFeeCategories = feeCategoriesFromResponse.filter(category =>
            allFeeCategoryIds.includes(category._id)
        );
        
        // If no categories found in response, try from feeStructureInfo as fallback
        if (applicableFeeCategories.length === 0 && this.component.state.feeStructureInfo?.length > 0) {
            applicableFeeCategories = this.component.state.feeStructureInfo.filter(category =>
                allFeeCategoryIds.includes(category._id)
            );
        }

        // Combine all fee types from all schedules
        const allStepOneData = feeStructureDetails.reduce((acc, schedule) => {
            acc.push(...(schedule.stepOne || []));
            return acc;
        }, []);

        const allStepTwoData = feeStructureDetails.reduce((acc, schedule) => {
            acc.push(...(schedule.stepTwo || []));
            return acc;
        }, []);

        // Get unique fee type IDs
        const newFeeTypes = allStepOneData.map(feeType => feeType.feeTypeId);

        // Get all fee types from applicable categories
        const allFeeTypes = applicableFeeCategories.reduce((acc, category) => {
            if (Array.isArray(category?.feeTypes)) {
                acc.push(...category.feeTypes);
            }
            return acc;
        }, []);

        // Filter fee types and remove duplicates based on highest totalFee
        const feeTypeMap = new Map();
        allFeeTypes.forEach(feeType => {
            if (!newFeeTypes.includes(feeType._id)) return;

            // Check if this fee type has an updated totalFee in allStepOneData
            const updatedFeeData = allStepOneData.find(stepOneData => stepOneData.feeTypeId === feeType._id);
            const currentTotalFee = updatedFeeData?.totalFee !== undefined ? updatedFeeData.totalFee : (feeType.totalFee || 0);

            const existingFeeType = feeTypeMap.get(feeType._id);
            const existingTotalFee = existingFeeType ? 
                (existingFeeType.updatedTotalFee !== undefined ? existingFeeType.updatedTotalFee : (existingFeeType.totalFee || 0)) : 0;

            if (!existingFeeType || currentTotalFee > existingTotalFee) {
                feeTypeMap.set(feeType._id, {
                    ...feeType,
                    totalFee: currentTotalFee, 
                   
                });
            }
        });

        const uniqueFeeTypes = Array.from(feeTypeMap.values());
        const oneTimeFees = uniqueFeeTypes.filter(fee => fee.isOneTime) || [];
        const regularFees = uniqueFeeTypes.filter(fee => !fee.isOneTime) || [];

        // Map installment data using the installments array from selectedFeePlan
        const installmentData = selectedFeePlan.installments
            ?.map((installment, index) => {
                const installmentFeeTypes = allStepTwoData.filter(fee =>
                    fee.installmentId === installment?.installmentId && !fee?.isOneTime
                ) || [];

                // If regularFees is empty, use the fee data directly from allStepTwoData
                const processedFeeTypes = regularFees.length > 0 
                    ? regularFees.map(feeType => {
                        const matchingFee = installmentFeeTypes.find(fee => fee.feeTypeId === feeType._id);
                        const finalTotalFee = feeType.updatedTotalFee !== undefined ? feeType.updatedTotalFee : (matchingFee?.totalFee || feeType.totalFee || 0);
                        
                        return {
                            ...feeType,
                            totalFee: finalTotalFee,
                            percentage: matchingFee?.termWisePercentageOrValue || 0,
                            calculatedFee: matchingFee?.termWiseActualValue || 0
                        };
                    })
                    : installmentFeeTypes.map(fee => ({
                        _id: fee.feeTypeId,
                        feeType: fee.feeType,
                        totalFee: fee.totalFee || 0,
                        percentage: fee.termWisePercentageOrValue || 0,
                        calculatedFee: fee.termWiseActualValue || 0,
                        isTransportFeeType: fee.isTransportFeeType || false,
                        priority: fee.priority || 0
                    }));

                return {
                    installment: `Installment ${index + 1}`,
                    installmentId: installment.installmentId,
                    dueDate: new Date(installment.date || Date.now()),
                    feeTypes: processedFeeTypes
                };
            }) || [];

        // Initialize one-time fees
        const oneTimeFeeSection = oneTimeFees?.map(feeType => {
            const stepTwoEntry = allStepTwoData.find(
                step => step.feeTypeId === feeType._id && step.isOneTime
            );

            // Use the updated totalFee if available, otherwise use the original totalFee
            const finalTotalFee = feeType.updatedTotalFee !== undefined ? feeType.updatedTotalFee : (stepTwoEntry?.totalFee || feeType.totalFee || 0);

            return {
                ...feeType,
                percentage: 100,
                calculatedFee: stepTwoEntry?.totalFee || stepTwoEntry?.termWiseActualValue || 0,
                totalFee: finalTotalFee,
                dueDate: stepTwoEntry?.dueDate || null,
                installmentId: stepTwoEntry?.installmentId || null,
                paymentTerm: stepTwoEntry?.paymentTerm || 'One Time'
            };
        });

        // Create selected fee structure array for multiple categories
        const selectedFeeStructure = applicableFeeCategories.map(category => {
            const categoryFeeTypes = uniqueFeeTypes.filter(feeType =>
                category.feeTypes.some(catFeeType => catFeeType._id === feeType._id)
            );

            return {
                ...category,
                feeTypes: categoryFeeTypes
            };
        }).filter(category => category.feeTypes.length > 0);

        this.component.setState({
            selectedFeePlan,
            installmentData,
            oneTimeFees: oneTimeFeeSection,
            selectedFeeStructure,
            isShowInstallmentInfo: true,
        });
    };

    handleFeeSubmission = async () => {
        try {
            const { installmentData, selectedFeeStructure, oneTimeFees } = this.component.state;
            const isValid = StudentAdmissionChangeUtils.validateInstallmentPercentages(installmentData, this.component.showToast);
            if (!isValid) return;

            // Get fee structure details array from the new structure
            const feeStructureDetails = this.component.state.rowData?.newFeeData?.feeSchedules|| [];

            if (feeStructureDetails.length === 0) {
                this.component.showToast('error', 'Error', 'No fee schedules found');
                return;
            }

            // Get common data
            const commonData = {
                academicYear: localStorage.getItem('userAcademicYear'),
                status: true,
                planId: this.component.state.selectedFeePlan._id,
                branchId: this.component.state.rowData?.user_info?.branchId
            };

            // Create updated fee schedules array based on user modifications
            const updatedFeeStructureDetails = feeStructureDetails.map(originalSchedule => {
                // Find the corresponding category for this fee structure
                const correspondingCategory = selectedFeeStructure.find(category =>
                    category._id === originalSchedule.feeCategory
                ) || selectedFeeStructure[0];

                if (!correspondingCategory) {
                    return originalSchedule; // Return original if no corresponding category found
                }

                // Get fee type IDs for this specific category
                const categoryFeeTypeIds = correspondingCategory.feeTypes.map(feeType => feeType._id);

                // Filter installmentData to only include fee types from this category
                const filteredInstallmentData = installmentData.map(installment => ({
                    ...installment,
                    feeTypes: installment.feeTypes.filter(feeType =>
                        categoryFeeTypeIds.includes(feeType._id || feeType.feeTypeId)
                    )
                })).filter(installment => installment.feeTypes.length > 0);

                // Filter oneTimeFees to only include fee types from this category
                const filteredOneTimeFees = (oneTimeFees || []).filter(feeType =>
                    categoryFeeTypeIds.includes(feeType._id || feeType.feeTypeId)
                );

                // Process stepOne data for this specific category
                const stepOne = correspondingCategory.feeTypes?.map((feeType, index) => ({
                    feeType: feeType.feeType,
                    admissionType: feeType.admissionType || [],
                    feeTypeId: feeType._id ?? feeType?.feeTypeId,
                    admissionTypes: feeType.admissionTypes || [],
                    priority: index + 1,
                    totalFee: feeType.totalFee
                }));

                // Process stepTwo data for this specific category
                let stepTwo = [];

                // Add regular installment fees
                filteredInstallmentData.forEach((installment, installmentIndex) => {
                    installment.feeTypes.forEach(feeType => {
                        if (feeType.percentage > 0) {
                            stepTwo.push({
                                feeType: feeType.feeType,
                                feeTypeId: feeType._id ?? feeType?.feeTypeId,
                                term: installmentIndex + 1,
                                paymentTerm: `Installment ${installmentIndex + 1}`,
                                totalFee: feeType.totalFee,
                                isDifferentialFee: feeType.isDifferentialFee || false,
                                isTransportFeeType: feeType.isTransportFeeType || false,
                                admissionType: feeType.admissionType || [],
                                priority: feeType.priority || 1,
                                termWisePercentageOrValue: feeType.percentage,
                                termWiseActualValue: feeType.calculatedFee,
                                installmentId: installment.installmentId,
                                dueDate: installment.dueDate,
                                isOneTime: false
                            });
                        }
                    });
                });

                // Add one-time fees for this category
                filteredOneTimeFees.forEach(feeType => {
                    stepTwo.push({
                        feeType: feeType.feeType,
                        feeTypeId: feeType._id ?? feeType?.feeTypeId,
                        isOneTime: true,
                        paymentTerm: "One Time",
                        totalFee: feeType.totalFee,
                        isDifferentialFee: feeType.isDifferentialFee || false,
                        isTransportFeeType: feeType.isTransportFeeType || false,
                        admissionType: feeType.admissionType || [],
                        priority: feeType.priority || 1,
                        termWisePercentageOrValue: 100,
                        termWiseActualValue: feeType.totalFee,
                        installmentId: feeType.installmentId,
                        dueDate: feeType.dueDate,
                        term: 1
                    });
                });

                return {
                    feeStructureId: originalSchedule.feeStructureId,
                    feeCategory: correspondingCategory._id,
                    scheduleName: correspondingCategory.structureName,
                    stepOne: stepOne,
                    stepTwo: StudentAdmissionChangeUtils.sortStepTwoData(stepTwo)
                };
            });

            // Create the payload with updated fee schedules
            const payload = {
                ...commonData,
                feeStructureDetails: updatedFeeStructureDetails
            };

            // Update rowData with the new fee schedule structure
            this.component.setState(prevState => ({
                rowData: {
                    ...prevState.rowData,
                    newFeeData: {
                        ...prevState.rowData.newFeeData,
                        feeStructureDetails: updatedFeeStructureDetails
                    }
                }
            }), () => {
                this.component.acceptedData(this.component.state?.rowData);
            });

        } catch (error) {
            this.component.showToast('error', 'Error', error.message || 'An error occurred while creating fee schedule');
        }
    };
}
